import { Promise as BPromise } from 'bluebird';
import jwtDecode from 'jwt-decode';
import Keycloak from 'keycloak-js';
import {
  KEYCLOAK_CLIENT_ID,
  KEYCLOAK_REALM_NAME,
  KEYCLOAK_SERVER_URL
} from './config';
import { authSignIn, authSignOut as signOut } from './redux/slices/auth';
import store from './redux/store';
import {
  decrypt_local,
  encrypt_local,
  setAllLocalStorageKeys
} from './utils/encryption';
import axios from './utils/axios';
import logger from './utils/logger';
import { StringKeys } from './types/base';
import { DEFAULT_APPLICATION_ROUTE, LANDING_PAGE } from './utils/constants';
import { getBrowsersLanguage } from './utils/utils';

export const KC_TOKEN = 'kc_token';
export const REALM_NAME = 'realm_name';
export const KC_REALM_NAME = 'td';
export const SKIP_REALM_DOMAIN = 'skp';
export const REALM_DOMAIN = 'realm_domain';
export const CLIENT_ID = 'client_id';
export const USER_EMAIL_ADDR = 'user_email_addr';
export const USER_LOGGED_IN = 'isLogin';
export const KC_REFRESH_TOKEN = 'kc_refreshToken';
export const IDLE_SESSION_TIME = 'idle_session_time';
export const KCC_TOKEN = 'kcc_token';
export const KCC_PARAMS = 'kcc_params';
export const KC_ID_TOKEN = 'kc_id_token';
export const IS_NEW_USER = 'new_user';
export const PERSIST_ROOT = 'persist:root';
export const AD_TOKEN = 'ad_token';
export const LANG = 'lang';

export const getKcLang = () => {
  return window.localStorage.getItem(LANG)
    ? window.localStorage.getItem(LANG)
    : null;
};

export const setKcLang = (lang: string) =>
  window.localStorage.setItem(LANG, lang);

export const getKcToken = () => {
  return window.localStorage.getItem(KC_TOKEN)
    ? decrypt_local(window.localStorage.getItem(KC_TOKEN))
    : null;
};
export const setKcToken = (token: string) =>
  window.localStorage.setItem(KC_TOKEN, encrypt_local(token));

export const getKcIdToken = () => {
  return window.localStorage.getItem(KC_ID_TOKEN)
    ? decrypt_local(window.localStorage.getItem(KC_ID_TOKEN))
    : null;
};
export const setKcIdToken = (token: string) =>
  window.localStorage.setItem(KC_ID_TOKEN, encrypt_local(token));

export const getUserLogInStatus = () =>
  window.localStorage.getItem(USER_LOGGED_IN)
    ? decrypt_local(window.localStorage.getItem(USER_LOGGED_IN))
    : null;
export const setUserLogInStatus = (login: string) =>
  window.localStorage.setItem(USER_LOGGED_IN, encrypt_local(login));

export const getRefreshToken = () => {
  return window.localStorage.getItem(KC_REFRESH_TOKEN)
    ? decrypt_local(window.localStorage.getItem(KC_REFRESH_TOKEN))
    : null;
};
export const setRefreshToken = (token: string) =>
  window.localStorage.setItem(KC_REFRESH_TOKEN, encrypt_local(token));

export const getIdleSessionTime = () =>
  window.localStorage.getItem(IDLE_SESSION_TIME)
    ? decrypt_local(window.localStorage.getItem(IDLE_SESSION_TIME))
    : '';
export const setIdleSessionTime = (time: string) =>
  window.localStorage.setItem(IDLE_SESSION_TIME, encrypt_local(time));

export const getKccToken = () =>
  window.localStorage.getItem(KCC_TOKEN)
    ? decrypt_local(window.localStorage.getItem(KCC_TOKEN))
    : '';
export const setKccToken = (token: string) =>
  window.localStorage.setItem(KCC_TOKEN, encrypt_local(token));

export const setEncryptParams = (params: any) => {
  window.localStorage.setItem(
    KCC_PARAMS,
    encrypt_local(JSON.stringify(params))
  );
};

export const setAdToken = (token: string) => {
  localStorage.setItem(AD_TOKEN, encrypt_local(token));
};

export const getAdToken = () => {
  return localStorage.getItem(AD_TOKEN)
    ? decrypt_local(localStorage.getItem(AD_TOKEN))
    : '';
};
export const getEncryptParams = () => {
  let params = window.localStorage.getItem(KCC_PARAMS)
    ? decrypt_local(window.localStorage.getItem(KCC_PARAMS))
    : null;
  return params ? JSON.parse(params) : params;
};

export const getNewUser = () =>
  window.localStorage.getItem(IS_NEW_USER)
    ? decrypt_local(window.localStorage.getItem(IS_NEW_USER))
    : '';
export const setNewUser = (new_user: string) =>
  window.localStorage.setItem(IS_NEW_USER, encrypt_local(new_user));

// Tenant Related information

export const getRealmName = () => {
  return window.localStorage.getItem(REALM_NAME)
    ? decrypt_local(window.localStorage.getItem(REALM_NAME))
    : null;
};

export const getKCRealmName = () => {
  return window.localStorage.getItem(KC_REALM_NAME) || null;
};

export const setKCRealmName = (realmName: string) => {
  window.localStorage.setItem(KC_REALM_NAME, realmName);
};

export const removeKCRealmName = () => {
  window.localStorage.removeItem(KC_REALM_NAME);
};

export const setRealmName = (realmName: string) => {
  window.localStorage.setItem(REALM_NAME, encrypt_local(realmName));
};

export const setSkipRealmDomain = (domain: string) => {
  window.localStorage.setItem(SKIP_REALM_DOMAIN, encrypt_local(domain));
};

export const getSkipRealmDomain = () => {
  return window.localStorage.getItem(SKIP_REALM_DOMAIN)
    ? decrypt_local(window.localStorage.getItem(SKIP_REALM_DOMAIN))
    : null;
};

export const removeSkipRealmDomain = () => {
  window.localStorage.removeItem(SKIP_REALM_DOMAIN);
};

export const setRealmDomain = (domain: string) => {
  window.localStorage.setItem(REALM_DOMAIN, encrypt_local(domain));
};

export const getRealmDomain = () => {
  return window.localStorage.getItem(REALM_DOMAIN)
    ? decrypt_local(window.localStorage.getItem(REALM_DOMAIN))
    : null;
};

export const removeRealmDomain = () => {
  window.localStorage.removeItem(REALM_DOMAIN);
};

export const getClientId = () => {
  return window.localStorage.getItem(CLIENT_ID)
    ? decrypt_local(window.localStorage.getItem(CLIENT_ID))
    : null;
};

export const setClientId = (clientId: string) =>
  window.localStorage.setItem(CLIENT_ID, encrypt_local(clientId));

export const getUserEmailAddress = () => {
  return window.localStorage.getItem(USER_EMAIL_ADDR)
    ? decrypt_local(window.localStorage.getItem(USER_EMAIL_ADDR))
    : null;
};
export const setUserEmailAddress = (user_email_addr: string) =>
  window.localStorage.setItem(USER_EMAIL_ADDR, encrypt_local(user_email_addr));

// Tenant Related information ends

export const isValidToken = (accessToken: string): boolean => {
  if (!accessToken) {
    return false;
  }
  const decoded: any = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

export let kc = new Keycloak({
  // getRealmName is managed by Egpt client and GetKCRealmName is managed by Keycloak to ensure correct realm domain
  realm: getRealmName() || getKCRealmName() || KEYCLOAK_REALM_NAME,
  url: `${getDomainUrl()}`,
  clientId: getClientId() || KEYCLOAK_CLIENT_ID
});

let kcPromise: any;
let updateRetries: number = 0;
let kcInitRetries: number = 0;

export const fetchADAccessToken = async () => {
  const kcToken = getKcToken();
  const decoded: any = jwtDecode(kcToken);
  const realmUrlParts = decoded.iss.split('/');
  const realmName = realmUrlParts[realmUrlParts.length - 1];
  if (decoded.idp_alias) {
    const response = await axios.get(
      `${getDomainUrl()}/realms/${realmName || KEYCLOAK_REALM_NAME}/broker/${
        decoded.idp_alias
      }/token`,
      {
        headers: {
          Authorization: `Bearer ${kcToken}`
        }
      }
    );
    return response?.data ? response?.data : null;
  }
  return null;
};

const saveTokens = (kc: Keycloak) => {
  setKcToken(kc.token);
  setRefreshToken(kc.refreshToken);
  setKcIdToken(kc.idToken);
  if (getUserLogInStatus() === null) {
    // in case of login redirect back to home page.
    //   window.location.href = "/";
    //   setUserLogInStatus("null");
    // } else if (getUserLogInStatus() === "null") {
    setUserLogInStatus('false');
  } else if (getUserLogInStatus() === 'false') {
    setUserLogInStatus('true');
  }
  setKccToken('true');
  setRealmName(kc.realm);
  setRealmDomain(window.location.host);
};

const clearToken = () => {
  const kcRealmName = getKCRealmName();
  const kcLang = getKcLang();
  localStorage.clear();
  if (kcRealmName) {
    setKCRealmName(kcRealmName);
  }
  if(kcLang){
    setKcLang(kcLang);
  }
};

// const isRefreshTimeExpired = (kcToken: string) => {
//   if (!kcToken) {
//     return false;
//   }
//   let token: any = jwtDecode(kcToken);
//   let tokenLifespanMins: any = (token.exp - token.iat) / 60;
//   let tokenRefreshTime: any = tokenLifespanMins * 0.5; // 70% of token life span
//   let tokenRemainingTime: any = (token.exp - new Date().getTime() / 1000) / 60;
//   let tokenElapsedTime = tokenLifespanMins - tokenRemainingTime;
//   return tokenElapsedTime > tokenRefreshTime ? true : false;
// };

// export const initializeTimer = (kcToken: string) => {
//   // let kcTokenMins: any = jwtDecode(kcToken);
//   // kcTokenMins = ((kcTokenMins.exp - kcTokenMins.iat) / 60) * intervalTime;
//   // kcTokenMins = kcTokenMins * 0.7; // 70% of token life span
//   // tokenRefreshTimer = setTimeout(() => updateToken(kc), kcTokenMins);

//   /////////////////////////////////////////////////////////
//   let token: any = jwtDecode(kcToken);
//   let tokenLifespanMins: any = (token.exp - token.iat) / 60;
//   let tokenRefreshTime: any = tokenLifespanMins * 0.1; // 70% of token life span
//   let tokenRemainingTime: any = (token.exp - new Date().getTime() / 1000) / 60;
//   let tokenElapsedTime = tokenLifespanMins - tokenRemainingTime;

//   if (tokenRemainingTime > 0) {
//     if (tokenElapsedTime > tokenRefreshTime) {
//       // update Token
//       updateToken(kc);
//     } else {
//       tokenRefreshTime = tokenRefreshTime - tokenElapsedTime;
//       // set timer with tokenrefresh time and update the call
//       tokenRefreshTimer = setTimeout(
//         () => updateToken(kc),
//         tokenRefreshTime * intervalTime
//       );
//     }
//   } else {
//     // immediate update the token
//     updateToken(kc);
//   }

//   ///////////////////////////////////////////////////////////
// };

const LOCK_KEY = 'token_refresh_lock';
const LOCK_EXPIRATION = 10000; // 10 seconds

function isLockActive() {
  const lock = JSON.parse(localStorage.getItem(LOCK_KEY) || '{}');
  const currentTime = Date.now();
  return lock.timestamp && currentTime - lock.timestamp < LOCK_EXPIRATION;
}

function acquireLock() {
  localStorage.setItem(LOCK_KEY, JSON.stringify({ timestamp: Date.now() }));
}

function releaseLock() {
  localStorage.removeItem(LOCK_KEY);
}

/**
 * Sync this tab's Keycloak instance with tokens from localStorage.
 * Call when another tab has refreshed and saved new tokens, so this tab
 * uses the new tokens instead of stale in-memory ones (which would cause
 * refresh to fail with single-use refresh tokens).
 */
function syncKcFromStorage() {
  const token = getKcToken();
  const refreshToken = getRefreshToken();
  const idToken = getKcIdToken();
  if (!token || !refreshToken) {
    return;
  }
  try {
    kc.token = token;
    kc.refreshToken = refreshToken;
    if (idToken) {
      kc.idToken = idToken;
    }
    logger.log('Synced Keycloak from localStorage (updated by another tab)');
  } catch (e) {
    logger.log('syncKcFromStorage failed', e);
  }
}

let storageListenerAdded = false;

function addTokenStorageListener() {
  if (storageListenerAdded) return;
  storageListenerAdded = true;
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === KC_TOKEN || e.key === KC_REFRESH_TOKEN) {
      if (getKcToken()) {
        syncKcFromStorage();
      }
    }
  });
}

function getDomainUrl() {
  const domain = getRealmDomain();
  if (domain) {
    const parsedUrl: URL = new URL(KEYCLOAK_SERVER_URL);
    try {
      const [_, port] = parsedUrl.host.split(':');
      parsedUrl.host = port ? `${domain}:${port}` : domain;
      return parsedUrl.toString();
    } catch {
      throw new Error(
        `Called outside browser context, unable to process URL: ${KEYCLOAK_SERVER_URL}`
      );
    }
  } else return KEYCLOAK_SERVER_URL;
}

function getCustomRouteUrlFromDomainUrl() {
  const domainUrl = getDomainUrl();
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(domainUrl);
    parsedUrl.pathname = DEFAULT_APPLICATION_ROUTE;
    return parsedUrl.toString();
  } catch {
    throw new Error(
      `Called outside browser context, unable to process URL: ${domainUrl}`
    );
  }
}

export const updateToken = async (kc: any, forceRefresh = false): Promise<boolean> => {
  // If another tab already refreshed, use the token from localStorage so this tab
  // doesn't call refresh with a stale (possibly single-use) refresh token.
  // Skip this check when forceRefresh is true — needed when called from onTokenExpired
  // with timeSkew:600, where the real exp hasn't passed but KC considers it expired.
  if (!forceRefresh && isValidToken(getKcToken())) {
    syncKcFromStorage();
    return true;
  }

  const anyNav: any = navigator;

  if (!anyNav.locks?.request) {
    return doUpdateToken(kc);
  }

  return new Promise((resolve) => {
    anyNav.locks.request('update_token', async () => {
      try {
        const success = await doUpdateToken(kc);
        resolve(success);
      } catch {
        resolve(false);
      }
    });
  });
};

async function doUpdateToken(kc: any): Promise<boolean> {
  logger.log('Token refresh: checking lock');

  if (isLockActive()) {
    logger.log('Another tab has already refreshed the token; syncing from storage.');
    syncKcFromStorage();
    return true;
  }

  acquireLock();

  try {
    await kc.updateToken(-1);

    saveTokens(kc);
    updateRetries = 0;
    releaseLock();

    logger.log(`Token refreshed ${new Date()}`);
    store.dispatch(authSignIn());

    return true;
  } catch (err) {
    releaseLock();

    if (updateRetries >= 3) {
      updateRetries = 0;
      kc.clearToken();
      store.dispatch(signOut());
      logger.log('Token refresh failed');
      return false;
    }

    updateRetries++;
    return doUpdateToken(kc);
  }
}
// export const initializeKcWithLocalstorage = (kc: any) => {
//   const token: any = getKcToken();
//   const refreshToken: any = getRefreshToken();
//   const idToken: any = getKcIdToken();

//   kc.onTokenExpired = () => {
//     logger.log(`token expired ${new Date()}`);
//     updateToken(kc);
//   };

//   kc.init({
//     token: token,
//     refreshToken: refreshToken,
//     idToken: idToken,
//     timeSkew: 600,
//   });
//   logger.log(kc);
// };

export const kcinit = (kcParams?: StringKeys) => {
  addTokenStorageListener();

  if (kcParams) {
    kc = new Keycloak({
      realm: kcParams.realm,
      url: getDomainUrl(),
      clientId: kcParams.clientId
    });
  }
  if (kcPromise && kcPromise.then) {
    // if already authunticating and got a request from some other app
    return kcPromise;
  }
  let token: any = null;
  let refreshToken: any = null;

  kcPromise = new Promise((res: any) => {
    const kcc_params = window.localStorage.getItem(KCC_PARAMS);
    if (kcc_params && kcc_params.length && kcc_params[0] === '{') {
      setAllLocalStorageKeys();
    }

    if (kcc_params !== null) {
      token = getKcToken();
      refreshToken = getRefreshToken();
    }
    // token expired
    kc.onTokenExpired = () => {
      logger.log(`token expired ${new Date()}`);
      updateToken(kc, true);
    };

    // Pass IDP hint to login page
    const kcLogin = kc.login;

    const login: typeof kcLogin = (options) => {
      const urlParams = new URLSearchParams(window.location.search);
      const idpHint = urlParams.get('kc_idp_hint');
      return kcLogin({
        ...options,
        idpHint,
        loginHint: kcParams?.userEmail || null,
        locale: getKcLang() || getBrowsersLanguage()
      });
    };
    kc.login = login;
    const initPromise = new BPromise((resInit: any) => {
      kcInitRetries = 0;
      kc.init({
        onLoad: 'login-required',
        token,
        refreshToken,
        timeSkew: 600, // token expires 10 min early
        checkLoginIframe: false,
        redirectUri:
          window.location.pathname === LANDING_PAGE ||
          window.location.pathname === '/signup'
            ? `${getCustomRouteUrlFromDomainUrl()}`
            : window.location.pathname
      })
        .then(
          (authenticated) => {
            if (kc.isTokenExpired()) {
              void updateToken(kc).then(() => {
                res(kc);
                resInit();
              });
              return;
            }
            if (authenticated) {
              saveTokens(kc);
              res(kc);
              kcInitRetries = 0;
            } else {
              clearToken();
              store.dispatch(signOut());
            }
            resInit();
          },
          (error) => {
            logger.log('Error: ', error);
            if (kcInitRetries < 3) {
              kcInitRetries++;
              kcinit();
            } else if (kcInitRetries >= 3) {
              alert('Login failed!, please retry!');
              res(kc);
              clearToken();
              store.dispatch(signOut());
              resInit();
            }
          }
        )
        .catch((err: any) => {
          logger.log('Code is Expired!', err);
          if (kcInitRetries < 3) {
            kcInitRetries++;
            kcinit();
          } else if (kcInitRetries >= 3) {
            alert('Login failed!, please retry!');
            res(kc);
            clearToken();
            store.dispatch(signOut());
            resInit();
          }
        });
      // if (isValidToken(token)) {
      //   kc.init({
      //     token: getKcToken(),
      //     refreshToken: getRefreshToken(),
      //     idToken: getKcIdToken(),
      //   }).then(
      //     (authenticated) => {
      //       if (kc.isTokenExpired()) {
      //         res(updateToken(kc));
      //         return;
      //       }
      //       if (authenticated) {
      //         res(kc);
      //       } else {
      //         clearToken();
      //         store.dispatch(signOut());
      //       }
      //       resInit();
      //     },
      //     (error) => {
      //       logger.log("Error: ", error);
      //       if (kcInitRetries < 3) {
      //         kcInitRetries++;
      //         kcinit();
      //       } else if (kcInitRetries >= 3) {
      //         alert("Login failed!, please retry!");
      //         res(kc);
      //         clearToken();
      //         store.dispatch(signOut());
      //         resInit();
      //       }
      //     }
      //   );
      // } else {
      // }
    });
    setTimeout(() => {
      // if auth server not found, keycloak may silently fail
      if (initPromise.isPending()) {
        clearToken();
        store.dispatch(signOut());
      }
    }, 15000);
  });
  return kcPromise;
};

export default kc;
